import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ServiceDefinition } from '~/constants/services/types';
import { PostTypeReel, PostTypeShort, PostTypeStory } from '~/post/constants';

import {
  Gif,
  Image,
  Link,
  Retweet,
  SourceLink,
  Video,
} from '~/composer/composer/entities/factories';
import {
  AttachmentTypes,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_INSTAGRAM,
  SERVICE_STARTPAGE,
  SERVICE_YOUTUBE,
  Services,
} from '~/constants';
import { BufferEditor } from '~/editor/BufferEditor/types.plate';
import { FileUploadConfig } from '~/uploads/constants';
import countHashtagsInText from '../../lib/validation/HashtagCounter';
import { MENTION_REGEX } from '../../utils/custom-plugins/mention';
import { EditorStateProxy } from '../EditorStateProxy/EditorStateProxy';
import { DraftMethods } from './DraftMethods';
import { ChannelData, ChannelDataProperties } from './types';
import { getYoutubeDefaultsFromStorage } from '../../components/youtube/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

export class Draft {
  /**
   * Unique identifier; currently that's the service name
   */
  id: string;

  /**
   * Type to differentiate between different update types in one channel
   * (e.g. reel or post for IG).
   * The mobile apps team uses a `post_type` property that is similar,
   * but Publish doesn't know anything about this field. Instead,
   * Publish relies on `updateType`.
   */
  updateType: string | null = null;

  /**
   * Service data structure in AppConstants.js
   */
  service: ServiceDefinition;

  editorState: BufferEditor;

  /**
   * Urls contained in the text
   * @type {string[]}
   */
  urls: string[] = [];

  /**
   * Urls unshortened by user
   * @type {string[]}
   */
  unshortenedUrls: string[] = [];

  /**
   * Link Attachment; data structure in getNewLink()
   */
  link: Link | null = null;

  /**
   * Thumbnail of media actively considered for Media Attachment
   */
  tempImage = null;

  /**
   * Media Attachment (type: image); data structure in getNewImage()
   */
  images: Image[] = [];

  availableImages: Image[] = [];

  /**
   * Media Attachment (type: video); data structure in getNewVideo()
   */
  video: Video | null = null;

  /**
   * Reference to the media object being actively edited
   */
  attachedMediaEditingPayload = null;

  /**
   * Media Attachment (type: gif); data structure in getNewGif()
   */
  gif: Gif | null = null;

  /**
   * Data structure in getNewRetweet();
   */
  retweet: Retweet | null = null;

  /**
   * Only updated for services w/ char limit
   * @type {number|null}
   */
  characterCount: number | null = null;

  /**
   * Only updated for services w/ comment char limit
   * @type {number|null}
   */
  characterCommentCount: number | null = null;

  /**
   * Only updated for services w/ title char limit
   * @type {number|null}
   */
  characterTitleCount: number | null = null;

  isEnabled = false;

  /**
   * Map of uploaderInstance <> integer(0-100)
   */
  filesUploadProgress = new Map();

  /**
   * @type {AttachmentTypes}
   */
  enabledAttachmentType: string | null = null;

  /**
   * Source url and page metadata; data structure in getNewSourceLink()
   */
  sourceLink: SourceLink | null = null;

  isSaved = false;

  hasSavingError = false;

  shortLinkLongLinkMap = new Map();

  scheduledAt = null;

  /**
   * null when scheduledAt is null; true/false otherwise
   */
  isPinnedToSlot: boolean | null = null;

  instagramFeedback: string[] = [];

  locationId = null;

  locationName: string | null = null;

  isTaggingPageLocation = null;

  shopgridLink: string | null = null;

  title: string | null = null;

  /**
   * Object holding data for channel-specific fields (e.g. GBP)
   */
  channelData: ChannelData | null = null;

  commentText: string | null = null;

  composerSidebarVisible = false;

  thread:
    | {
        text: string;
        enabledAttachmentType: string | null;
        link: Link | null;
        images: Image[];
        video: Video | null;
        gif: Gif | null;
        retweet: Retweet | null;
      }[]
    | null = null;

  constructor(service: ServiceDefinition, editorState: BufferEditor) {
    this.id = service.name;
    this.service = service;
    this.editorState = editorState;
    this.characterCount = service.charLimit === null ? null : 0;
    this.characterCommentCount = service.commentCharLimit === null ? null : 0;
    this.characterTitleCount = service.titleCharLimit === null ? null : 0;
    this.enabledAttachmentType = service.canHaveAttachmentType(
      AttachmentTypes.MEDIA
    )
      ? AttachmentTypes.MEDIA
      : null;

    if (service.isGoogleBusiness()) {
      this.updateType = 'whats_new';
      this.channelData = {
        googlebusiness: {
          start_date: dayjs.utc().startOf('day').unix(),
          end_date: dayjs.utc().add(7, 'day').startOf('day').unix(),
        },
      };
    }

    if (service.isInstagram()) {
      this.updateType = 'post';
      this.channelData = {
        instagram: {
          share_to_feed: false,
        },
      };
    }

    if (service.isStartPage()) {
      this.channelData = {
        startPage: {},
      };
    }

    if (service.isYoutube()) {
      const defaultChannelData = getYoutubeDefaultsFromStorage();
      this.updateType = PostTypeShort;
      this.channelData = {
        youtube: {
          title: '',
          category: defaultChannelData
            ? defaultChannelData.category
            : { id: '', title: '' },
          license: defaultChannelData ? defaultChannelData.license : 'youtube',
          privacyStatus: defaultChannelData
            ? defaultChannelData.privacyStatus
            : 'public',
          notifySubscribers: defaultChannelData
            ? defaultChannelData.notifySubscribers
            : true,
          embeddable: defaultChannelData ? defaultChannelData.embeddable : true,
          madeForKids: defaultChannelData
            ? defaultChannelData.madeForKids
            : false,
        },
      };
    }
  }

  isEnabledOrOmni(): boolean {
    return this.isEnabled || this.service.isOmni;
  }

  isEmpty(): boolean {
    return (
      this.isTextEmpty() &&
      (this.enabledAttachmentType !== AttachmentTypes.LINK ||
        this.link === null) &&
      (this.canHaveMedia() || this.images.length === 0) &&
      (this.canHaveMedia() || this.video === null) &&
      (this.canHaveMedia() || this.gif === null) &&
      (this.enabledAttachmentType !== AttachmentTypes.RETWEET ||
        this.retweet === null) &&
      this.sourceLink === null
    );
  }

  hasLinkAttachmentEnabled(): boolean {
    return DraftMethods.hasLinkAttachmentEnabled(this);
  }

  hasMediaAttachmentEnabled(): boolean {
    return DraftMethods.hasMediaAttachmentEnabled(this);
  }

  hasRetweetAttachmentEnabled(): boolean {
    return DraftMethods.hasRetweetAttachmentEnabled(this);
  }

  hasLinkAttachment(): boolean {
    return DraftMethods.hasLinkAttachment(this);
  }

  hasLinkAttachmentWithThumbnail(): boolean {
    return DraftMethods.hasLinkAttachmentWithThumbnail(this);
  }

  hasMediaAttachment(): boolean {
    return DraftMethods.hasMediaAttachment(this);
  }

  hasRetweetAttachment(): boolean {
    return DraftMethods.hasRetweetAttachment(this);
  }

  hasAttachment(): boolean {
    return DraftMethods.hasAttachment(this);
  }

  hasNoMediaAttached(): boolean {
    return DraftMethods.hasNoMediaAttached(this);
  }

  hasVideoAttachment(): boolean {
    return DraftMethods.hasVideoAttachment(this);
  }

  hasImagesAttachment(): boolean {
    return DraftMethods.hasImagesAttachment(this);
  }

  isReelsPost(): boolean {
    return this.service.isInstagram() && this.updateType === PostTypeReel;
  }

  isStoryPost(): boolean {
    return this.service.isInstagram() && this.updateType === PostTypeStory;
  }

  isShortPost(): boolean {
    return this.service.isYoutube() && this.updateType === PostTypeShort;
  }

  getUploadConfig(): FileUploadConfig {
    let uploadFormatsConfig = new Map(FileUploadConfig.MEDIA);
    if (this.isReelsPost()) {
      uploadFormatsConfig = new Map(FileUploadConfig.REELS);
    }
    if (this.isStoryPost()) {
      uploadFormatsConfig = new Map(FileUploadConfig.STORIES);
    }
    if (this.isShortPost()) {
      uploadFormatsConfig = new Map(FileUploadConfig.SHORTS);
    }

    this.service.unavailableMediaAttachmentTypes.forEach((mediaType) => {
      uploadFormatsConfig.delete(mediaType);
    });

    return uploadFormatsConfig;
  }

  hasGifAttachment(): boolean {
    return this.hasMediaAttachmentEnabled() && this.gif !== null;
  }

  canAttachMoreImages(): boolean {
    return (
      this.hasMediaAttachmentEnabled() &&
      this.service.maxAttachableImagesCount > this.images.length
    );
  }

  canAttachMoreMedia(): boolean {
    return (
      this.canAttachMoreImages() &&
      !this.hasVideoAttachment() &&
      !this.hasGifAttachment()
    );
  }

  hasUploadsInProgress(): boolean {
    return this.filesUploadProgress.size > 0;
  }

  getTotalImageUploadsRemaining(): number {
    const maxImages = this.service.maxAttachableImagesCount;
    const attachedImages = this.images?.length || 0;
    return maxImages - attachedImages;
  }

  hasText(): boolean {
    return DraftMethods.hasText(this);
  }

  hasRequiredAttachmentAttached(): boolean {
    return (
      this.service.requiredAttachmentType === null ||
      (this.service.requiredAttachmentType === AttachmentTypes.LINK &&
        this.hasLinkAttachment()) ||
      (this.service.requiredAttachmentType === AttachmentTypes.RETWEET &&
        this.hasRetweetAttachment()) ||
      (this.service.requiredAttachmentType === AttachmentTypes.MEDIA &&
        this.hasAttachment())
    );
  }

  hasMediaAttachmentSwitch(): boolean {
    return (
      !this.hasMediaAttachmentEnabled() &&
      this.service.canHaveSomeAttachmentType([
        AttachmentTypes.LINK,
        AttachmentTypes.RETWEET,
      ])
    );
  }

  hasLinkAttachmentSwitch(): boolean {
    return this.hasMediaAttachmentEnabled() && this.link !== null;
  }

  hasRetweetAttachmentSwitch(): boolean {
    return this.hasMediaAttachmentEnabled() && this.retweet !== null;
  }

  hasAttachmentSwitch(): boolean {
    return (
      this.hasMediaAttachmentSwitch() ||
      this.hasLinkAttachmentSwitch() ||
      this.hasRetweetAttachmentSwitch()
    );
  }

  canType(): boolean {
    return !this.isStoryPost();
  }

  canTypeMoreCharacters(): boolean {
    return (
      this.service.charLimit !== null &&
      this.characterCount !== null &&
      (this.service.charLimit - this.characterCount <= 280 ||
        !this.service.isFacebook())
    );
  }

  canTypeMoreCommentCharacters(): boolean {
    return (
      this.service.commentCharLimit !== null &&
      this.characterCommentCount !== null &&
      this.service.commentCharLimit - this.characterCommentCount <= 280
    );
  }

  isTextEmpty(): boolean {
    return EditorStateProxy.getPlainText(this.editorState).length === 0;
  }

  canHaveMedia(): boolean {
    return this.enabledAttachmentType !== AttachmentTypes.MEDIA;
  }

  getNumberOfMentionsInText(): number {
    const captionText = EditorStateProxy.getPlainText(this.editorState);
    const matchesInCaption = captionText.match(MENTION_REGEX);
    return matchesInCaption !== null ? matchesInCaption.length : 0;
  }

  getNumberOfMentionsInComment(): number {
    const commentText = this.commentText || '';
    const matchesInComment = commentText.match(MENTION_REGEX);
    return matchesInComment !== null ? matchesInComment.length : 0;
  }

  getNumberOfMentions(): number {
    return (
      this.getNumberOfMentionsInText() + this.getNumberOfMentionsInComment()
    );
  }

  getNumberOfHashtagsInText(): number {
    const captionText = EditorStateProxy.getPlainText(this.editorState);
    return countHashtagsInText(captionText);
  }

  getNumberOfHashtagsInComment(): number {
    const commentText = this.commentText || '';
    return countHashtagsInText(commentText);
  }

  getNumberOfHashtags(): number {
    return (
      this.getNumberOfHashtagsInText() + this.getNumberOfHashtagsInComment()
    );
  }

  getAttachmentThumbnails(): string[] | null {
    let thumbnails: string[] | null = null;

    if (this.hasVideoAttachment() && this.video !== null) {
      thumbnails = [this.video.thumbnail];
    } else if (
      this.hasGifAttachment() &&
      this.gif !== null &&
      this.gif.stillGifUrl !== null
    ) {
      thumbnails = [this.gif.stillGifUrl];
    } else if (this.hasImagesAttachment()) {
      thumbnails = this.images.map((image) => image.url);
    } else if (
      this.hasLinkAttachmentWithThumbnail() &&
      this.link !== null &&
      this.link.thumbnail !== null
    ) {
      thumbnails = [this.link.thumbnail.url];
    } else if (this.hasRetweetAttachment() && this.retweet !== null) {
      thumbnails = [this.retweet.avatarUrl];
    }

    return thumbnails;
  }

  isVerticalVideoOnlySupported(): boolean {
    const serviceConfig = Services.get(this.service.name);
    return (
      (serviceConfig && serviceConfig.onlyAllowsVerticalVideo) ||
      this.isReelsPost()
    );
  }

  isImageFirst(): boolean {
    return (
      this.service.usesImageFirstLayout &&
      (this.hasNoMediaAttached() || this.isVerticalVideoOnlySupported())
    );
  }

  canHaveChannelData(): boolean {
    return [
      SERVICE_GOOGLEBUSINESS,
      SERVICE_INSTAGRAM,
      SERVICE_STARTPAGE,
      SERVICE_YOUTUBE,
    ].includes(this.id);
  }

  setChannelData(channelData?: ChannelDataProperties | null): void {
    if (!this.canHaveChannelData()) {
      this.channelData = null;
      return;
    }

    this.channelData = channelData ? { [this.id]: channelData } : {};
  }

  canHaveThread(): boolean {
    return this.updateType === 'thread';
  }

  hasThread(): boolean {
    return Array.isArray(this.thread) && this.thread.length > 1;
  }
}
export interface SlateDraft extends Draft {
  editorState: BufferEditor;
}

export default Draft;
